import { MirrorData, SetMirrorData } from '@type/job-add';
import { TailwindSpinner } from '@components/common';
import {
  Label, Input, RadioText, Button,
  RecordInterface, SetFormValues,
} from '@components/common/Forms';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useJobSalary } from '@components/templates/job-add/edit/hooks/useJobSalary';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { dataTestId } from '@services/data-test-id';

/** Props */
interface JobSalaryProps {
  locale: Locale,
  record: RecordInterface,
  setFormValues: SetFormValues,
  apiSalaryMin?: number,
  apiSalaryMax?: number,
  salaryMinInputName?: string,
  salaryMaxInputName?: string,
  confidentialInputName?: string,
  labelDescriptionClass?: string,
  reqMirrorData?: Pick<MirrorData, 'title' | 'addresses' | 'description' | 'careerLevels' | 'employmentTypes' | 'workPlacement'>,
  setMirrorData?: SetMirrorData,
  hasInitialSalary?: boolean,
  hasInitialConfidential?: boolean,
}

/**
 * @description Job Salary component. To be used in a form.
 * - `locale` - the current app locale
 * - `record` - record function from useBjForm
 * - `setFormValues` - setFormValues function from useBjForm
 * - `apiSalaryMin` - initial min salary value from API
 * - `apiSalaryMax` - initial max salary value from API
 * - `salaryMinInputName` - name of the min input; default is 'salaryMin'
 * - `salaryMaxInputName` - name of the max input; default is 'salaryMax'
 * - `confidentialInputName` - name of the confidential input; default is 'salaryConfidential'
 * - `labelDescriptionClass` - additional classes for the label description
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData` - function to update the mirror form state
 * - `hasInitialSalary` - if the job has an initial salary
 * - `hasInitialConfidential` - if the job has an initial confidential state
 * - `jobSuggestedSalary` - the job suggested salary
 * - `isJobSuggestedSalaryLoading` - if the job suggested salary is loading
 * - `showConfidentialWarning` - if we should show the confidential warning
 */
export const JobSalary = (props: JobSalaryProps) => {
  // Destructure props
  const {
    locale,
    record,
    setFormValues,
    apiSalaryMin = 0,
    apiSalaryMax = 0,
    salaryMinInputName = 'salaryMin',
    salaryMaxInputName = 'salaryMax',
    confidentialInputName = 'salaryConfidential',
    labelDescriptionClass = '',
    reqMirrorData,
    setMirrorData,
    hasInitialSalary,
    hasInitialConfidential,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Initial salary string
  // **************************
  const initialSalary = useMemo(() => {
    let holder = '';
    if (apiSalaryMin) {
      holder = `${apiSalaryMin}${apiSalaryMax && apiSalaryMax !== apiSalaryMin ? ` - ${apiSalaryMax}` : ''}`;
    }
    return holder;
  }, [apiSalaryMin, apiSalaryMax]);

  // Use job salary hook
  // **************************
  const {
    hasCustomError,
    customErrorMessage,
    handleSalaryChange,
    salaryObject,
    addSuggestedSalary,
    jobSuggestedSalary,
    isJobSuggestedSalaryLoading,
    setUserChangedSalary,
    showConfidentialityWarning,
    setShowConfidentialityWarning,
  } = useJobSalary({
    locale,
    setFormValues,
    apiSalaryMin,
    apiSalaryMax,
    salaryMinInputName,
    salaryMaxInputName,
    reqMirrorData,
    hasInitialSalary,
    hasInitialConfidential,
  });

  // Render
  // **************************
  return (
    <div className="mt-4">
      <div className="md:flex md:gap-1">
        <Label
          required
          description={t('job.add.label.net_salary')}
          descriptionClass={labelDescriptionClass}
          className="!mt-0"
        >
          {/* Salary input group */}
          <span className="flex flex-wrap">
            {/* Full salary - just for UI, we remove it from the submitted data */}
            <Input
              required
              type="text"
              className="pr-18"
              pattern="^\d+(\s?-\s?\d+)?$"
              patternMismatchMessage={t('job.add.salary.pattern-mismatch-message')}
              customValidityMessages={{ customError: customErrorMessage }}
              placeholder={t('job.add.salary.placeholder')}
              hasCustomError={hasCustomError}
              onBlur={() => setMirrorData?.()}
              {...record('salary', {
                removeFromSubmit: true,
                // for the saved jobs, prevent something like 800 - 800 as API makes it, just show 800
                ...(apiSalaryMin && { defaultValue: initialSalary }),
              })}
              onChange={(e) => {
                setUserChangedSalary(true);
                handleSalaryChange(e);
              }}
            />

            {/* Currency */}
            <span className="z-1 -ml-16 flex w-16 shrink-0 p-px text-sm font-semibold">
              <span className="flex w-full items-center justify-center rounded-r border-l bg-surface-50 pt-px">
                EUR
              </span>
            </span>
          </span>

          {/* Min salary - hidden */}
          <Input
            type="hidden"
            value={salaryObject.salaryMin}
            {...record(salaryMinInputName, {
              defaultValue: undefined,
            })}
          />

          {/* Max salary - hidden */}
          <Input
            type="hidden"
            value={salaryObject.salaryMax}
            {...record(salaryMaxInputName, {
              defaultValue: undefined,
            })}
          />
        </Label>

        {/* Confidential */}
        <label
          htmlFor={confidentialInputName}
          className="ml-3 mt-3 flex cursor-pointer items-start text-sm md:mt-12"
        >
          <Input
            className="text-primary-light checked:bg-primary-light"
            type="checkbox"
            onChange={(e) => {
              setShowConfidentialityWarning(e.target.checked);
              setMirrorData?.();
            }}
            {...record(confidentialInputName, {
              convertValue: 'toBoolean',
              defaultValue: 'true',
            })}
          />
          <RadioText>
            {t('job.add.label.salary_confidential')}
          </RadioText>
        </label>
      </div>

      {/* Suggested salary */}
      {jobSuggestedSalary && jobSuggestedSalary?.min && (
        <div className="mt-3 flex items-start gap-1.5 rounded bg-secondary/10 p-2 text-sm">
          {isJobSuggestedSalaryLoading && (
            <TailwindSpinner className="size-5 shrink-0 text-secondary" />
          )}
          <div className={`grow${isJobSuggestedSalaryLoading ? ' pointer-events-none opacity-60 saturate-0' : ''}`}>
            <span>{t('job.add.suggested_salary_label2')}</span>
            <span className="mx-1 font-semibold">
              {`${jobSuggestedSalary?.min ?? 0} - ${jobSuggestedSalary?.max ?? 0}`}
            </span>
            <span>EUR.</span>
            <Button
              styling="text"
              size="sm"
              className="mx-1 !font-semibold"
              onClick={() => {
                addSuggestedSalary(jobSuggestedSalary);
                setMirrorData?.();
              }}
              {...dataTestId('jobAddPage.jobSalary.addSuggestedSalaryButton')}
            >
              {`${t('apply.suggested.salary')}.`}
            </Button>
          </div>
        </div>
      )}

      {/* Confidential warning */}
      {showConfidentialityWarning && (
        <div className="mt-3 flex items-start gap-1.5 rounded bg-warning/10 p-2 text-sm">
          <ExclamationTriangleIcon className="size-5 shrink-0 text-warning" />
          {t('job.add.salary.confidential.hint')}
        </div>
      )}
    </div>
  );
};
